import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { useToast } from '@workspace/ui/components/toast'
import { Check, ChevronDown, Clock, Pencil, Plus, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { trackEvent } from '../../../../lib/gtag'
import { copy } from '../../copy/investor-copy'
import {
  useEditFollow,
  useFollowStartupMutation,
  useStartupRelationship,
} from '../../hooks/use-investor-assistant'
import { ActionMenu } from '../action-menu'
import { FollowWindow } from './follow-window'
import { followToast } from './relationship-cards'
import { StopDialog } from './stop-dialog'

type Overlay = 'follow' | 'edit' | 'stop' | null

/**
 * The one Follow control of a startup's profile, for a signed-in investor: Follow, Following,
 * Waiting or ended. A follow is made from the link the investor holds, so a startup in stealth
 * can be followed from its link and from nowhere else.
 */
export function FollowControl({
  startupId,
  startupName,
  shareToken,
  quiet = false,
}: {
  startupId: string
  startupName: string
  shareToken: string
  /** Grey instead of orange, while the investor writes a note: the note is the one thing to do. */
  quiet?: boolean
}) {
  const query = useStartupRelationship(startupId)
  const follow = useFollowStartupMutation()
  const edit = useEditFollow()
  const [overlay, setOverlay] = useState<Overlay>(null)
  const button = useRef<HTMLButtonElement>(null)
  const toast = useToast()
  const close = () => setOverlay(null)

  // A startup the investor may not read yet (stealth, reached by link) is simply not followed.
  const data = query.data
  const state = data?.state ?? 'NONE'
  if (query.isLoading) return null

  const waiting = state === 'WAITING_CHECK' || state === 'WAITING_PLACE'
  const following = state === 'ACTIVE' || state === 'CONDITION_MET'
  const vars = { startup: startupName }
  const FollowButton = quiet ? SecondaryButton : CtaButton

  return (
    <>
      {state === 'NONE' && (
        <FollowButton ref={button} size="sm" icon={<Plus />} onClick={() => setOverlay('follow')}>
          {copy('page.follow')}
        </FollowButton>
      )}
      {state === 'ENDED_BY_FOUNDER' && (
        <SecondaryButton size="sm" disabled>
          {copy('page.ended')}
        </SecondaryButton>
      )}
      {(following || waiting) && (
        <ActionMenu
          label={copy(following ? 'page.following' : 'page.waiting')}
          className="w-72"
          actions={[
            {
              key: 'edit',
              icon: Pencil,
              title: copy('menu.editFollow'),
              hint: copy('menu.editFollow.hint'),
              onSelect: () => setOverlay('edit'),
            },
            {
              key: 'stop',
              icon: X,
              title: copy('menu.stopFollowing'),
              hint: copy('menu.stopFollowing.hint', vars),
              destructive: true,
              onSelect: () => setOverlay('stop'),
            },
          ]}
          trigger={
            <SecondaryButton ref={button} size="sm" icon={following ? <Check /> : <Clock />}>
              <span className="inline-flex items-center gap-1.5">
                {copy(following ? 'page.following' : 'page.waiting')}
                <ChevronDown />
              </span>
            </SecondaryButton>
          }
        />
      )}

      {overlay === 'follow' && (
        <FollowWindow
          open
          mode="follow"
          anchor={button.current}
          startupName={startupName}
          allowanceLine={
            data
              ? copy(
                  data.allowance.plan === 'FREE' ? 'card.allowance.free' : 'card.allowance.paid',
                  {
                    used: data.allowance.used,
                    limit: data.allowance.limit,
                  }
                )
              : undefined
          }
          pending={follow.isPending}
          onOpenChange={(open) => !open && close()}
          onSubmit={(condition) =>
            follow.mutate(
              { body: { shareToken, ...(condition && { condition }) } },
              {
                onSuccess: (response) => {
                  trackEvent('follow_started', {
                    startup_id: startupId,
                    with_condition: !!condition,
                    surface: 'page',
                  })
                  toast(followToast(response.data, startupName))
                  close()
                },
                onError: () => toast(copy('toast.follow.error', { startup: startupName })),
              }
            )
          }
        />
      )}
      {overlay === 'edit' && data?.follow && (
        <FollowWindow
          open
          mode="edit"
          anchor={button.current}
          startupName={startupName}
          initialCondition={data.follow.condition}
          pending={edit.isPending}
          onOpenChange={(open) => !open && close()}
          onSubmit={(condition) =>
            edit.mutate(
              { path: { startupId }, body: { condition } },
              {
                onSuccess: () => {
                  trackEvent('follow_edited', {
                    startup_id: startupId,
                    with_condition: !!condition,
                  })
                  toast(copy('toast.edit.saved'))
                  close()
                },
                onError: () => toast(copy('toast.edit.error')),
              }
            )
          }
        />
      )}
      {overlay === 'stop' && (
        <StopDialog
          startupId={startupId}
          startupName={startupName}
          waiting={waiting}
          onClose={close}
        />
      )}
    </>
  )
}
